import { Observable, from, of } from 'rxjs';
import { BackEndRequest } from './../actions';
import { DatabagActionDescriptor } from '../reducer';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, first, mergeMap } from 'rxjs/operators';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { ResourceData } from '../../elements/resource/resource.types';
import { AuthenticationService } from '../../authentication/authentication.service';
import { APIResponse } from '../../services/canvas/canvas.service';
import { APIConfig } from '../../api/api-config.provider';

export interface Response<T> {
  success: boolean;
  error?: any;
  data: T;
}

export enum RequestType {
  GET = 'get',
  POST = 'post',
  PATCH = 'patch',
  DELETE = 'delete',
}
export interface RequestProcess<T = any> {
  loading: Observable<boolean>;
  error: Observable<any>;
  data: Observable<T>;
}

export interface APIRespone<T = any> {
  data?: T;
  error?: any;
}

@Injectable()
export class HttpService {
  private session: CognitoUserSession;
  constructor(
    private httpClient: HttpClient,
    private authService: AuthenticationService,
    private readonly apiConfig: APIConfig,
  ) {
    this.authService.session.subscribe(session => (this.session = session));
  }
  convertWebmToMp4(file: File): Observable<Blob> {
    const formData = new FormData();
    formData.append('video', file);

    const token = this.session?.getAccessToken().getJwtToken();
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${this.apiConfig.api.url}/videos/convert`;

    return this.httpClient.post(url, formData, {
      headers,
      responseType: 'blob', // important to get binary mp4 file
    });
  }

  async getResourceByUUID<T = any>(
    path: string,
    uuid: string,
  ): Promise<ResourceData<T>> {
    const result = await this.getPromise(path, {
      resourceData: {
        IRI: `http://nowords.com#${uuid}`,
      },
    });
    return result[0];
  }

  get<T>(
    path: string,
    descriptor?: DatabagActionDescriptor | string,
    params?: Record<string, any>,
  ) {
    const request = {
      type: RequestType.GET,
      path,
      params: this.prepareParams(params),
    };
    return this.getObservable(path, params);
  }

  prepareParams(params: Record<string, any>) {
    Object.entries(params || {}).map(([key, value]) => {
      params[key] = typeof value === 'object' ? JSON.stringify(value) : value;
    });
    return params;
  }

  getObservable<T = any>(path: string, params?: Record<string, any>) {
    return from(this.authService.getCurrentSession())
      .pipe(
        mergeMap(session =>
          this.httpClient.get<T>(this.apiConfig.api.url + path, {
            params: this.prepareParams(params),
            headers: {
              Authorization: `Bearer ${session.getAccessToken().getJwtToken()}`,
            },
          }),
        ),
      )
      .pipe(first(p => !!p));
  }

  async getPromise(path: string, params?: Record<string, any>) {
    return this.getObservable(path, params).toPromise();
  }

  postRecording(audioBlob: Blob, fileName: string) {
    // -- // -- //
    const formData = new FormData();
    formData.append('file', audioBlob);
    formData.append('fileName', fileName);

    const url = `${this.apiConfig.api.url}/audio/upload-recording`;

    const token = this.session?.getAccessToken().getJwtToken();
    const headers = {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
      Authorization: 'Bearer ' + token,
    };

    console.log('post-recording', url);
    this.httpClient.post(url, formData, { headers }).subscribe(res => {
      console.log('res', res);
    });
  }

  getToken() {
    return this.session?.getAccessToken().getJwtToken();
  }

  async _postPromise<T>(path: string, body: any): Promise<any> {
    return new Promise((res, rej) => {
      this.requestCall({
        type: RequestType.POST,
        body,
        path,
      })
        .pipe(
          catchError(e => {
            rej(e);
            return of(e);
          }),
        )
        .subscribe(data => {
          if (data) {
            res({ data });
          }
        });

      // .pipe(
      //   map(response => res(response)),
      //   catchError(err => {
      //     rej(err.message);
      //     return of(null);
      //   }),
      // );
    });
  }

  // private request<T extends any>(
  //   request: BackEndRequest,
  //   descriptor: DatabagActionDescriptor | string = 'x',
  // ) {
  //   const action = getGeneralAction(request, descriptor);
  //   const path = typeof descriptor == 'string' ? descriptor : descriptor.path;
  //   this.store.setLoading(path);

  //   this.store
  //     .get(action.id)
  //     .subscribe((value: { error: any; path: string; data: any }) => {
  //       if (value) {
  //         const { path } = value;
  //         this.store.resetLoading(path);
  //       }
  //     });
  //   this.store.dispatch(action);
  //   return this.store.get<Response<T>>(action.id);
  // }

  patch(req: Partial<BackEndRequest>) {
    return this.requestCall({
      type: RequestType.PATCH,
      ...req,
    });
  }

  post(path: string, body: any, headers?: any) {
    return this.requestCall({
      type: RequestType.POST,
      path,
      body,
      headers,
    });
  }

  requestCall(req: BackEndRequest): Observable<any> {
    const { path, body, params, headers: _headers } = req;
    // const { token } = req;
    const url = `${this.apiConfig.api.url}/${path}`;
    // const url = `${environment.backend.url}/${path}`;
    // const url = `http://localhost:3001/${path}`;

    // const url = `https://iodptiajg3.execute-api.eu-central-1.amazonaws.com/${path}`;
    // const token = this.session?.getAccessToken().getJwtToken();
    const idToken = this.session?.getIdToken().getJwtToken();

    const headers = {
      Authorization: 'Bearer ' + idToken,
      ...(_headers || {}),
    };
    switch (req.type) {
      case RequestType.GET:
        return this.httpClient.get(url, {
          params,
          headers,
        });
      case RequestType.POST:
        return this.httpClient.post(url, body, { headers });
      case RequestType.PATCH:
        return this.httpClient.patch(url, body, { headers });
      case RequestType.DELETE:
        return this.httpClient.delete(url, { params, headers, body });
      default:
        throw new Error(`${req.type} is not a valid type`);
    }
  }

  async processImage(base64data: string): Promise<APIResponse<string>> {
    try {
      const token = this.session?.getAccessToken().getJwtToken();
      await this.httpClient
        .post<{ uuid: string }>(
          `${this.apiConfig.api.url}/image-processing/x`,
          base64data,
          {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Access-Control-Allow-Methods':
                'GET, POST, PATCH, PUT, DELETE, OPTIONS',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .toPromise();

      return {};
    } catch (error) {
      return { error: error.message };
    }
  }

  async saveFileToS3(file: File, uuid: string): Promise<APIResponse<string>> {
    try {
      const token = this.session?.getAccessToken().getJwtToken();

      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('uuid', uuid);
      await this.httpClient
        .post<{ uuid: string }>(`${this.apiConfig.api.url}/file`, formData, {
          headers: {
            // 'Access-Control-Allow-Origin': '*',
            // 'Access-Control-Allow-Methods':
            //   'GET, POST, PATCH, PUT, DELETE, OPTIONS',
            Authorization: `Bearer ${token}`,
          },
        })
        .toPromise();

      return {};
    } catch (error) {
      return { error: error.message };
    }
  }
  async saveImageToS3(
    base64data: string,
    uuid: string,
    extension: string,
  ): Promise<APIResponse<string>> {
    try {
      const token = this.session?.getAccessToken().getJwtToken();
      await this.httpClient
        .post<{ uuid: string }>(
          `${this.apiConfig.api.url}/file-upload`,
          {
            base64data,
            uuid,
            extension,
          },
          {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Access-Control-Allow-Methods':
                'GET, POST, PATCH, PUT, DELETE, OPTIONS',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .toPromise();

      return {};
    } catch (error) {
      return { error: error.message };
    }
  }

  requestBlob(data: FormData) {
    // -- //
    const token = this.session?.getAccessToken().getJwtToken();
    return this.httpClient.post(
      `${this.apiConfig.api.url}/ai/image/generate-titan`,
      data,
      {
        responseType: 'blob', // Backend returns image/jpeg
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  }
  imageCache: Record<string, string> = {};
  loadCache: Record<string, Promise<string>> = {};
  async searchImageByLabel(label: string) {
    const { error, data } = await this.get<ResourceData[]>('/editor/img', '_', {
      resourceData: {
        literals: { label },
      },
    })
      .pipe(first(p => !!p))
      .toPromise();
    if (error) {
      throw new Error(error);
    }
    return data;
  }
}
